import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Grid, Typography, useTheme } from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { COLORS } from '../theme/colors';
import PageLayout from '../components/layout/PageLayout';
import type { HeroContent, MissionVisionItem } from '../types/content';

type PurposeCardProps = {
  item: MissionVisionItem;
  icon: ReactNode;
  variant: 'mision' | 'vision';
};

function PurposeCard({ item, icon, variant }: PurposeCardProps): JSX.Element {
  const isVision = variant === 'vision';

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: { xs: 260, md: 300 },
        p: { xs: 3, md: 4 },
        borderRadius: '22px',
        position: 'relative',
        overflow: 'hidden',
        background:
          'linear-gradient(145deg, rgba(30,27,46,0.96) 0%, rgba(24,22,41,0.98) 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 22px 70px rgba(0,0,0,0.22)',
        transition: 'all 0.28s ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: isVision
            ? 'radial-gradient(circle at 86% 18%, rgba(216,180,254,0.13) 0%, transparent 38%)'
            : 'radial-gradient(circle at 16% 18%, rgba(168,85,247,0.13) 0%, transparent 38%)',
          opacity: 0.9,
          pointerEvents: 'none',
        },
        '&:hover': {
          transform: 'translateY(-5px)',
          borderColor: 'rgba(168,85,247,0.32)',
          boxShadow: '0 28px 90px rgba(147,51,234,0.18)',
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: '16px',
            background: isVision ? COLORS.purple[500] : COLORS.gradients.brand,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            boxShadow: '0 0 26px rgba(168,85,247,0.32)',
            color: '#fff',
            '& svg': {
              fontSize: 25,
            },
          }}
        >
          {icon}
        </Box>

        <Typography
          variant="h3"
          sx={{
            color: '#ffffff',
            fontSize: { xs: '1.35rem', md: '1.55rem' },
            fontWeight: 700,
            lineHeight: 1.3,
            mb: 2,
          }}
        >
          {item.label}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: COLORS.gray[400],
            fontSize: { xs: '0.98rem', md: '1.05rem' },
            lineHeight: 1.75,
            maxWidth: 520,
          }}
        >
          {item.description}
        </Typography>
      </Box>
    </Box>
  );
}

export default function MisionVisionPage(): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const hero = t('misionVision.hero', { returnObjects: true }) as Required<HeroContent>;
  const mision = t('misionVision.mision', { returnObjects: true }) as MissionVisionItem;
  const vision = t('misionVision.vision', { returnObjects: true }) as MissionVisionItem;

  return (
    <PageLayout>
      <Box
        sx={{
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
          background: isDark
            ? `
              radial-gradient(circle at 50% 4%, rgba(168,85,247,0.18) 0%, rgba(147,51,234,0.08) 28%, transparent 56%),
              radial-gradient(circle at 14% 44%, rgba(168,85,247,0.10) 0%, transparent 36%),
              radial-gradient(circle at 86% 58%, rgba(216,180,254,0.08) 0%, transparent 34%),
              linear-gradient(180deg, ${COLORS.dark[900]} 0%, ${COLORS.dark[800]} 48%, ${COLORS.dark[900]} 100%)
            `
            : `
              radial-gradient(circle at 50% 4%, rgba(168,85,247,0.14) 0%, rgba(216,180,254,0.10) 28%, transparent 56%),
              linear-gradient(180deg, ${COLORS.light[100]} 0%, ${COLORS.gray[100]} 100%)
            `,
          pt: { xs: 10, md: 14 },
          pb: { xs: 10, md: 14 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: { xs: -180, md: -260 },
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: 580, md: 980 },
            height: { xs: 580, md: 980 },
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(168,85,247,0.18) 0%, rgba(147,51,234,0.08) 34%, transparent 70%)',
            filter: 'blur(70px)',
            opacity: isDark ? 0.7 : 0.34,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: { xs: '42%', md: '38%' },
            left: { xs: '-180px', md: '-100px' },
            width: { xs: 360, md: 560 },
            height: { xs: 360, md: 560 },
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(168,85,247,0.12) 0%, rgba(216,180,254,0.06) 36%, transparent 74%)',
            filter: 'blur(68px)',
            opacity: isDark ? 0.52 : 0.26,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            bottom: { xs: -180, md: -240 },
            right: { xs: '-180px', md: '-100px' },
            width: { xs: 380, md: 620 },
            height: { xs: 380, md: 620 },
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(216,180,254,0.12) 0%, rgba(168,85,247,0.06) 36%, transparent 76%)',
            filter: 'blur(70px)',
            opacity: isDark ? 0.48 : 0.24,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              textAlign: 'center',
              maxWidth: 760,
              mx: 'auto',
              mb: { xs: 7, md: 10 },
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: {
                  xs: '2rem',
                  sm: '2.45rem',
                  md: '3.15rem',
                  lg: '3.45rem',
                },
                lineHeight: {
                  xs: 1.16,
                  md: 1.12,
                },
                fontWeight: 700,
                color: 'text.primary',
                mb: 2.5,
              }}
            >
              {hero.title1}{' '}
              <Box
                component="span"
                sx={{
                  display: { xs: 'block', sm: 'inline' },
                  background: COLORS.gradients.brand,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {hero.highlight}
              </Box>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: 580,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.15rem' },
                lineHeight: 1.7,
              }}
            >
              {hero.subtext}
            </Typography>
          </Box>

          <Box sx={{ position: 'relative' }}>
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                left: '50%',
                top: 24,
                bottom: 24,
                width: '1px',
                background: `linear-gradient(to bottom, transparent, ${COLORS.purple[500]}66, transparent)`,
                display: { xs: 'none', md: 'block' },
              }}
            />

            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: COLORS.purple[500],
                boxShadow: `0 0 22px ${COLORS.purple[400]}`,
                display: { xs: 'none', md: 'block' },
              }}
            />

            <Grid container spacing={{ xs: 3, md: 5 }} alignItems="center">
              <Grid item xs={12} md={5}>
                <PurposeCard
                  item={mision}
                  variant="mision"
                  icon={<RocketLaunchIcon />}
                />
              </Grid>

              <Grid item md={2} sx={{ display: { xs: 'none', md: 'block' } }} />

              <Grid item xs={12} md={5}>
                <Box sx={{ mt: { xs: 0, md: 7 } }}>
                  <PurposeCard
                    item={vision}
                    variant="vision"
                    icon={<VisibilityIcon />}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </PageLayout>
  );
}